import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useRoute } from '@react-navigation/native';
import { useSessionStore } from '../../shell/session/store/useSessionStore';
import { useAuthStore } from '../../auth/store/useAuthStore';
import { useBranchPermission } from '../../shell/session/hooks/useBranchPermission';
import { useModulePermission } from '../../shell/session/hooks/useModulePermission';
import { useOperationalConfirm } from '../../shared/ui/useOperationalConfirm';
import { pullPurchasesWhenNeeded } from '../application/pullPurchasesWhenNeeded';
import { pullExpensesWhenNeeded } from '../../sync/core/pullExpensesWhenNeeded';
import {
  createPurchaseOrderIntake,
  voidPurchaseOrderIntake,
  voidPurchaseReceptionIntake,
  voidPurchaseReturnIntake,
  closePurchaseOrderIntake,
  sendPurchaseOrderIntake,
  createPurchaseReceptionIntake,
  createPurchaseReturnIntake,
  createStockTransferIntake,
} from '../application/purchaseIntake';
import {
  listPurchaseOrdersForBranch,
  countPurchaseOrdersForBranch,
  getPurchaseOrderById,
  summarizePurchasesForBranch,
  listReceptionsForOrder,
  listReturnsForReception,
  listReturnItemsForReturn,
  hasActiveReturnForOrder,
} from '../infra/purchasesRepoRead';
import {
  type PurchaseOrderRow,
  type PurchaseOrderWithItems,
  type PurchaseOrderStatus,
  type PurchaseOrderOrigin,
  type PurchasesSummary,
  type PurchaseReceptionWithItems,
  type PurchaseReturnRow,
  PURCHASES_LIST_PAGE_SIZE,
} from '../domain/purchaseTypes';
import { PurchaseDomainError } from '../domain/purchaseErrors';
import {
  isToPayExpense,
  isOverdueExpense,
  purchaseOrderIdsForQueue,
  type PurchaseQueueFilter,
} from '../domain/purchaseQueues';
import {
  listExpensesByPurchaseOrderId,
  listExpensesForBranch,
  listLinkedPurchaseExpensesForBranch,
} from '../../expenses/infra/expensesRepo';
import {
  executeCreateExpenseIntake,
  executeSettleExpenseIntake,
  executeUpdateExpenseFiscalIntake,
} from '../../expenses/application/expenseIntake';
import { getFiscalProfile } from '../../fiscal/infra-api';
import {
  creditNoteDocTypeFromPurchaseInvoice,
  emptyExpenseFiscalDraft,
  type ExpenseFiscalDraft,
} from '../../expenses/domain-api';
import type { ExpenseFiscalInvoiceInput } from '../../expenses/domain/expenseTypes';
import { getSessionOrganizationId } from '../../shell/session/sessionOrganization';
import { expenseAmountResidual } from '../../expenses/domain/expenseLabels'
import { isoDateToPaidAt } from '../../expenses/components/expensePaymentFlow'
import { todayIsoDate } from '../../expenses/infra/expenseListSql'
import type { ExpenseRow, LinkedPurchaseExpenseRow } from '../../expenses/domain/expenseTypes'
import type { CreateReturnItemInput } from '../infra/purchasesRepoMutations'
import type { PurchaseDraftLine } from '../components/PurchaseDraftLineList'
import type { ExpenseSettleMethod } from '../../expenses/components/expenseSettlementMethods'
import type { ExpenseCategorySlug } from '../../expenses/domain/expenseCategories';
import {
  resolveCashSummary,
  applyCheckCommand,
  listChecksForBranch,
  type CashSessionRow,
  type CashSummary,
  type CheckRow,
} from '../../cash/infra-api';
import { useEffectiveBusinessCapabilities } from '../../shell/session/hooks/useEffectiveBusinessCapabilities';
import { newClientUuid } from '../../shared/newClientUuid';
import { captureCatch } from '../../shared/sentry/sentryUser';
import { parseDecimalInput } from '../../shared/numeric/localeNumeric';
import {
  showError,
  showInfo,
  showSuccess,
  showSuccessAfterModalClose,
  showErrorAfterModalClose,
} from '../../shared/ui/appToast';
import { getErrorMessage } from '../../shared/errorMessage';
import { presentUnexpectedFailure } from '../../shared/sentry/presentUnexpectedFailure';
import { reportUserVisibleFailure } from '../../shared/sentry/sentryUser';

import { formatMoney } from '../../shared/format/public-api';
export type PurchasesListState = {
  orders: PurchaseOrderRow[];
  total: number;
  loading: boolean;
  page: number;
  search: string;
  statusFilter: PurchaseOrderStatus | 'all';
};

const EMPTY_SUMMARY: PurchasesSummary = {
  totalOrders: 0,
  totalAmount: 0,
  pendingReception: 0,
  partialReception: 0,
  received: 0,
  cancelled: 0,
  toPay: 0,
  toPayAmount: 0,
  overdue: 0,
};

export function usePurchasesScreen() {
  const currentBranchId = useSessionStore((s) => s.currentBranchId);
  const { user } = useAuthStore();
  const userId = user?.id ?? null;

  const { canView, canEdit } = useBranchPermission('Purchases');
  const canReceive = useModulePermission('Purchases', 'receive');
  const canReturn = useModulePermission('Purchases', 'return');
  const canVoid = useModulePermission('Purchases', 'void');
  const canPay = useModulePermission('Expenses', 'pay');
  const canEndorseChecks = useModulePermission('Checks', 'endorse');

  // BUG-34: screenView removido (dead code — la UI nunca lo usaba)
  const [orders, setOrders] = useState<PurchaseOrderRow[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [search, setSearch] = useState('');
  const [pullReady, setPullReady] = useState(false);
  const [statusFilter, setStatusFilter] = useState<PurchaseOrderStatus | 'all'>('all');
  const [queueFilter, setQueueFilter] = useState<PurchaseQueueFilter>('all');
  const [originFilter, setOriginFilter] = useState<PurchaseOrderOrigin | 'all'>('all');
  // BUG-35 fix: filtro por proveedor expuesto en el filter sheet.
  const [supplierFilter, setSupplierFilter] = useState<string | 'all'>('all');
  // BUG-16: Sort state para la tabla
  const [sortBy, setSortBy] = useState<'date' | 'total' | 'supplier'>('date');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
  const route = useRoute<{
    key: string;
    name: string;
    params?: {
      openDirect?: boolean;
      initialQueueFilter?: PurchaseQueueFilter;
      focusToken?: string;
      openSupplierAccountId?: string;
      openSupplierAccountName?: string;
      openPurchaseOrderId?: string;
    };
  }>();
  const consumedFocusTokenRef = useRef<string | null>(null);
  const consumedQueueFocusTokenRef = useRef<string | null>(null);
  const consumedHandoffFocusTokenRef = useRef<string | null>(null);
  const [summary, setSummary] = useState<PurchasesSummary>(EMPTY_SUMMARY);

  const [registerOpen, setRegisterOpen] = useState(false);
  const [registerMode, setRegisterMode] = useState<'order' | 'direct'>('order');
  const [detailTarget, setDetailTarget] = useState<PurchaseOrderWithItems | null>(null);
  // OBS-6: Loading state al abrir detail
  const [detailLoading, setDetailLoading] = useState(false);
  // BUG-36: receptionTarget removido (dead state — la recepción usa detailTarget)
  const [returnTarget, setReturnTarget] = useState<PurchaseOrderWithItems | null>(null);
  // BUG-45: Recepciones y devoluciones ligadas a la orden abierta (con ítems para el detalle)
  const [detailReceptions, setDetailReceptions] = useState<PurchaseReceptionWithItems[]>([]);
  const [detailReturns, setDetailReturns] = useState<PurchaseReturnRow[]>([]);
  const [returnReceptions, setReturnReceptions] = useState<PurchaseReceptionWithItems[]>([]);
  const [transferOpen, setTransferOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [supplierAccountTarget, setSupplierAccountTarget] = useState<{ id: string | null; name: string } | null>(null);
  const [linkedExpense, setLinkedExpense] = useState<ExpenseRow | null>(null);
  // Todos los gastos activos ligados a la OC abierta (uno por recepción con gasto).
  const [linkedExpenses, setLinkedExpenses] = useState<ExpenseRow[]>([]);
  // Gastos ligados del branch (para KPIs de cola y pills de pago por fila).
  const [queueExpenses, setQueueExpenses] = useState<LinkedPurchaseExpenseRow[]>([]);
  // "Duplicar como borrador": prefill del register modal desde una OC anulada.
  const [registerPrefill, setRegisterPrefill] = useState<{
    supplierId: string | null;
    supplierName: string | null;
    items: PurchaseDraftLine[];
  } | null>(null);
  const [hasReturn, setHasReturn] = useState(false);
  const [settleOpen, setSettleOpen] = useState(false);
  const [settleAmountText, setSettleAmountText] = useState('');
  const [portfolioChecks, setPortfolioChecks] = useState<CheckRow[]>([]);
  const [portfolioChecksLoading, setPortfolioChecksLoading] = useState(false);
  const [activeCashSession, setActiveCashSession] = useState<CashSessionRow | null>(null);
  const [cashSummary, setCashSummary] = useState<CashSummary | null>(null);
  const [fiscalCaptureEnabled, setFiscalCaptureEnabled] = useState(false);
  const [fiscalCompleteExpense, setFiscalCompleteExpense] = useState<ExpenseRow | null>(null);
  const [supplierCreditNoteModal, setSupplierCreditNoteModal] = useState<{
    branchId: string;
    supplierId: string | null;
    supplierName: string | null;
    amount: number;
    initialDraft: ExpenseFiscalDraft;
  } | null>(null);

  /** Comprar ahora / nueva OC: resolver turno de caja al abrir el register (medio «efectivo de la caja»). */
  useEffect(() => {
    if (!registerOpen || !currentBranchId) return;
    let cancelled = false;
    void (async () => {
      try {
        const cash = await resolveCashSummary(currentBranchId);
        if (cancelled) return;
        setActiveCashSession(cash.activeSession ?? null);
        setCashSummary(cash);
      } catch (err) {
        if (cancelled) return;
        console.warn('[usePurchasesScreen] registerOpen cash', err);
        setActiveCashSession(null);
        setCashSummary(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [registerOpen, currentBranchId]);

  const checksCapabilityOn = useEffectiveBusinessCapabilities().has('checks');
  const canPayWithCheck = canPay && canEndorseChecks && checksCapabilityOn;

  const { confirm, ConfirmModal } = useOperationalConfirm();

  const branchRef = useRef(currentBranchId);
  branchRef.current = currentBranchId;

  useEffect(() => {
    if (!currentBranchId) {
      setFiscalCaptureEnabled(false);
      return;
    }
    let cancelled = false;
    void getFiscalProfile(currentBranchId)
      .then((profile) => {
        if (!cancelled) {
          setFiscalCaptureEnabled(profile?.taxRegime === 'responsable_inscripto');
        }
      })
      .catch(() => {
        if (!cancelled) setFiscalCaptureEnabled(false);
      });
    return () => {
      cancelled = true;
    };
  }, [currentBranchId]);

  // ─── Load orders ─────────────────────────────────────────────────────────
  const loadOrders = useCallback(async () => {
    if (!currentBranchId) return;
    setLoading(true);
    try {
      let queueOrderIds: string[] | undefined;
      if (queueFilter === 'to_pay' || queueFilter === 'overdue') {
        const expenses = await listLinkedPurchaseExpensesForBranch(currentBranchId);
        queueOrderIds = purchaseOrderIdsForQueue(queueFilter, expenses, todayIsoDate());
      }
      const [rows, count] = await Promise.all([
        listPurchaseOrdersForBranch({
          branchId: currentBranchId,
          search: search || undefined,
          status: statusFilter,
          queue: queueFilter,
          queueOrderIds,
          origin: originFilter,
          supplierId: supplierFilter,
          limit: PURCHASES_LIST_PAGE_SIZE,
          offset: page * PURCHASES_LIST_PAGE_SIZE,
          sortBy,
          sortDir,
        }),
        countPurchaseOrdersForBranch({
          branchId: currentBranchId,
          search: search || undefined,
          status: statusFilter,
          queue: queueFilter,
          queueOrderIds,
          origin: originFilter,
          supplierId: supplierFilter,
        }),
      ]);
      setOrders(rows);
      setTotal(count);
    } catch (err) {
      console.warn('[usePurchasesScreen] loadOrders', err);
      presentUnexpectedFailure({ module: 'purchases', op: 'load_orders', error: err, userMessage: 'No pude cargar los pedidos.' });
    } finally {
      setLoading(false);
    }
  }, [currentBranchId, search, statusFilter, queueFilter, originFilter, supplierFilter, page, sortBy, sortDir]);

  // ─── Load summary ────────────────────────────────────────────────────────
  const loadSummary = useCallback(async () => {
    if (!currentBranchId) return;
    try {
      const [s, expenses] = await Promise.all([
        summarizePurchasesForBranch(currentBranchId),
        listLinkedPurchaseExpensesForBranch(currentBranchId),
      ]);
      const today = todayIsoDate();
      setQueueExpenses(expenses);
      setSummary({
        ...s,
        toPay: expenses.filter(isToPayExpense).length,
        toPayAmount: expenses
          .filter(isToPayExpense)
          .reduce((sum, expense) => sum + Math.max(0, expense.amount - expense.amountPaid), 0),
        overdue: expenses.filter((expense) => isOverdueExpense(expense, today)).length,
      });
    } catch (err) {
      presentUnexpectedFailure({ module: 'purchases', op: 'load_summary', error: err, userMessage: 'No pude cargar el resumen.' });
    }
  }, [currentBranchId]);

  // ─── Bootstrap pull (termina antes de pintar la lista) ──────────────────
  useEffect(() => {
    if (!currentBranchId) return;
    let cancelled = false;
    setPullReady(false);
    void (async () => {
      await Promise.all([
        pullPurchasesWhenNeeded(currentBranchId, { force: true }),
        pullExpensesWhenNeeded(currentBranchId, { force: true }),
      ]);
      if (!cancelled) setPullReady(true);
    })();
    return () => {
      cancelled = true;
    };
  }, [currentBranchId]);

  // ─── Reload on filters/page change ───────────────────────────────────────
  useEffect(() => {
    if (!pullReady) return;
    void loadOrders();
  }, [loadOrders, pullReady]);

  // ─── Reload summary when orders change ───────────────────────────────────
  // pullReady como trigger: las orders pueden venir del cache de purchases
  // (poblado por el pull del Home) antes de que termine el pull de gastos,
  // entonces orders.length no cambia cuando pullReady flipa y el resumen
  // quedaría con colas de pago en 0.
  useEffect(() => {
    if (!pullReady) return;
    void loadSummary();
  }, [loadSummary, pullReady, orders.length]);

  // ─── Reset page when filters change ──────────────────────────────────────
  useEffect(() => {
    setPage(0);
  }, [search, statusFilter, queueFilter, originFilter, supplierFilter]);

  const handleStatusFilterChange = useCallback((next: PurchaseOrderStatus | 'all') => {
    setStatusFilter(next);
    setQueueFilter('all');
  }, []);

  const handleQueueFilterChange = useCallback((next: PurchaseQueueFilter) => {
    setQueueFilter((prev) => (prev === next ? 'all' : next));
    setStatusFilter('all');
  }, []);

  useEffect(() => {
    const params = route.params;
    if (params?.initialQueueFilter) {
      const token = params.focusToken ?? `queue-${params.initialQueueFilter}`;
      if (token === consumedQueueFocusTokenRef.current) return;
      consumedQueueFocusTokenRef.current = token;
      setQueueFilter(params.initialQueueFilter);
      setStatusFilter('all');
      return;
    }
    if (params?.openSupplierAccountId) {
      const token = params.focusToken ?? `account-${params.openSupplierAccountId}`;
      if (token !== consumedHandoffFocusTokenRef.current) {
        consumedHandoffFocusTokenRef.current = token;
        setSupplierAccountTarget({
          id: params.openSupplierAccountId,
          name: params.openSupplierAccountName ?? 'Proveedor',
        });
      }
    }
    if (params?.openPurchaseOrderId && pullReady) {
      const token = params.focusToken ?? `po-${params.openPurchaseOrderId}`;
      if (token !== consumedHandoffFocusTokenRef.current) {
        consumedHandoffFocusTokenRef.current = token;
        void (async () => {
          setDetailLoading(true);
          try {
            const full = await getPurchaseOrderById(params.openPurchaseOrderId!);
            if (full) setDetailTarget(full);
          } finally {
            setDetailLoading(false);
          }
        })();
      }
    }
    if (!params?.openDirect) return;
    const token = params.focusToken ?? 'openDirect';
    if (token === consumedFocusTokenRef.current) return;
    consumedFocusTokenRef.current = token;
    setRegisterMode('direct');
    setRegisterOpen(true);
  }, [route.params, pullReady]);

  // ─── Load function (for pull-to-refresh / focus) ─────────────────────────
  const loadFn = useCallback(async (opts?: { force?: boolean }) => {
    if (!currentBranchId) return;
    const force = opts?.force === true;
    await Promise.all([
      pullPurchasesWhenNeeded(currentBranchId, { force }),
      pullExpensesWhenNeeded(currentBranchId, { force }),
    ]);
    await Promise.all([loadOrders(), loadSummary()]);
  }, [currentBranchId, loadOrders, loadSummary]);

  // ─── Open detail ─────────────────────────────────────────────────────────
  const loadDetailExtras = useCallback(async (orderId: string) => {
    const [expenses, returned, receptions] = await Promise.all([
      listExpensesByPurchaseOrderId(orderId),
      hasActiveReturnForOrder(orderId),
      listReceptionsForOrder(orderId),
    ]);
    // Todos los gastos activos de la OC (puede haber varios, uno por recepción).
    setLinkedExpenses(expenses);
    // Para el flujo de pago: priorizar el pendiente más antiguo por vencimiento.
    const pendingExpense =
      [...expenses]
        .filter((e) => e.paymentStatus === 'unpaid' || e.paymentStatus === 'partial')
        .sort((a, b) => (a.dueDate ?? '9999-12-31').localeCompare(b.dueDate ?? '9999-12-31'))[0] ??
      expenses[0] ??
      null;
    setLinkedExpense(pendingExpense);
    setHasReturn(returned);
    // BUG-45: Cargar recepciones ligadas (solo activas para mostrar en el detail)
    setDetailReceptions(receptions.filter((r) => r.status === 'active'));
    // BUG-45: Cargar devoluciones ligadas a las recepciones activas
    const activeReceptions = receptions.filter((r) => r.status === 'active');
    const allReturns: PurchaseReturnRow[] = [];
    for (const rec of activeReceptions) {
      const recReturns = await listReturnsForReception(rec.id);
      allReturns.push(...recReturns);
    }
    setDetailReturns(allReturns);
  }, []);

  const handleOpenDetail = useCallback(async (order: PurchaseOrderRow) => {
    // OBS-6: Mostrar loading mientras carga el detalle
    setDetailLoading(true);
    try {
      const full = await getPurchaseOrderById(order.id);
      if (!full) {
        showError('Error', 'No encontré el pedido. Puede que se haya anulado en otro dispositivo.');
        return;
      }
      setDetailTarget(full);
      await loadDetailExtras(full.id);
    } catch (err) {
      showError('Error', 'No pude cargar el detalle del pedido.');
    } finally {
      setDetailLoading(false);
    }
  }, [loadDetailExtras]);

  // ─── Open supplier account ───────────────────────────────────────────────
  // BUG-37: handleOpenSupplierAccount (con args) removido — la UI solo usa el picker
  const handleOpenSupplierAccountPicker = useCallback(() => {
    setSupplierAccountTarget({ id: null, name: '' });
  }, []);

  // ─── Open return modal ───────────────────────────────────────────────────
  const handleOpenReturn = useCallback(async (order: PurchaseOrderWithItems) => {
    setReturnTarget(order);
    try {
      const receptions = await listReceptionsForOrder(order.id);
      setReturnReceptions(receptions);
    } catch (err) {
      console.warn('[usePurchasesScreen] handleOpenReturn', err);
      presentUnexpectedFailure({ module: 'purchases', op: 'load_receptions', error: err, userMessage: 'No pude cargar las recepciones del pedido.' });
    }
  }, []);

  // ─── Stock transfer ──────────────────────────────────────────────────────
  const handleStockTransfer = useCallback(
    async (params: Parameters<typeof createStockTransferIntake>[0]) => {
      setSubmitting(true);
      try {
        await createStockTransferIntake(params);
        setTransferOpen(false);
        showSuccessAfterModalClose('Transferencia registrada');
        await loadOrders();
        await loadSummary();
      } catch (err) {
        console.warn('[usePurchasesScreen] handleStockTransfer', err);
        showErrorAfterModalClose('Error', 'No pude registrar la transferencia.');
        reportUserVisibleFailure({ module: 'purchases', op: 'stock_transfer', error: err });
      } finally {
        setSubmitting(false);
      }
    },
    [loadOrders, loadSummary],
  );

  // ─── Create order ────────────────────────────────────────────────────────
  const handleCreateOrder = useCallback(
    async (params: Parameters<typeof createPurchaseOrderIntake>[0]) => {
      setSubmitting(true);
      try {
        const created = await createPurchaseOrderIntake(params);
        if (params.origin === 'direct') {
          // BUG-28: Si la recepción falla, la orden ya fue creada.
          // Atrapar el error por separado para dar feedback diferenciado.
          try {
            const receptionResult = await createPurchaseReceptionIntake({
              receptionId: crypto.randomUUID(),
              branchId: params.branchId,
              purchaseOrderId: created.purchaseOrderId,
              supplierId: params.supplierId,
              supplierName: params.supplierName,
              receivedBy: params.createdBy,
              receivedAt: new Date().toISOString(),
              items: params.items.map((item, index) => ({
                itemId: crypto.randomUUID(),
                purchaseOrderItemId: created.items[index]?.itemId ?? null,
                productId: item.productId ?? '',
                productName: item.productName,
                productSku: item.productSku,
                quantity: item.quantity,
                unitCost: item.unitCost,
                unitOfMeasure: item.unitOfMeasure,
                targetBranchId: item.targetBranchId ?? null,
                // #694: variante de destino del stock (null = parent)
                variantId: item.variantId ?? null,
              })),
              generateExpense: true,
              fiscal: params.directFiscal ?? null,
              // BUG-24: Usar el toggle del modal en lugar de hardcodear true
              alreadyPaid: params.alreadyPaid !== false,
              paymentOnReceive:
                params.alreadyPaid !== false ? params.paymentOnReceive ?? undefined : undefined,
            });
          } catch (receptionErr) {
            console.warn('[usePurchasesScreen] handleCreateOrder reception', receptionErr);
            setRegisterOpen(false);
            showErrorAfterModalClose(
              'Recepción fallida',
              'El pedido se creó pero no se pudo registrar la recepción. Lo encontrás en la lista con estado "Enviada".',
            );
            await loadOrders();
            await loadSummary();
            return;
          }
        }
        setRegisterOpen(false);
        showSuccessAfterModalClose(params.origin === 'direct' ? 'Compra registrada' : 'Pedido guardado');
        await loadOrders();
        await loadSummary();
      } catch (err) {
        console.warn('[usePurchasesScreen] handleCreateOrder', err);
        setRegisterOpen(false);
        showErrorAfterModalClose('Error', 'No pude guardar el pedido.');
        reportUserVisibleFailure({ module: 'purchases', op: 'create_order', error: err });
      } finally {
        setSubmitting(false);
      }
    },
    [loadOrders, loadSummary],
  );

  const handleSendOrder = useCallback(
    async (orderId: string) => {
      setSubmitting(true);
      try {
        await sendPurchaseOrderIntake(orderId, userId);
        setDetailTarget(null);
        showSuccessAfterModalClose('Pedido enviado');
        await loadOrders();
        await loadSummary();
      } catch (err) {
        console.warn('[usePurchasesScreen] handleSendOrder', err);
        showErrorAfterModalClose('Error', err instanceof PurchaseDomainError ? err.message : 'No pude enviar el pedido.');
        reportUserVisibleFailure({ module: 'purchases', op: 'send_order', error: err });
      } finally {
        setSubmitting(false);
      }
    },
    [userId, loadOrders, loadSummary],
  );

  // ─── Void order ──────────────────────────────────────────────────────────
  // BUG-11: El modal ya pide motivo (two-click flow), no necesita confirm() adicional.
  const handleVoidOrder = useCallback(
    async (orderId: string, voidReason: string) => {
      setSubmitting(true);
      try {
        await voidPurchaseOrderIntake({
          purchaseOrderId: orderId,
          voidedBy: userId ?? null,
          voidReason,
        });
        setDetailTarget(null);
        showSuccessAfterModalClose('Pedido anulado');
        await loadOrders();
        await loadSummary();
      } catch (err) {
        console.warn('[usePurchasesScreen] handleVoidOrder', err);
        showErrorAfterModalClose('Error', err instanceof PurchaseDomainError ? err.message : 'No pude anular el pedido.');
        reportUserVisibleFailure({ module: 'purchases', op: 'void_order', error: err });
      } finally {
        setSubmitting(false);
      }
    },
    [userId, loadOrders, loadSummary],
  );

  // ─── Receive order ───────────────────────────────────────────────────────
  const handleReceiveOrder = useCallback(
    async (params: Parameters<typeof createPurchaseReceptionIntake>[0]) => {
      setSubmitting(true);
      try {
        await createPurchaseReceptionIntake(params);
        const orderId = params.purchaseOrderId;
        await Promise.all([
          pullPurchasesWhenNeeded(params.branchId, { force: true }),
          pullExpensesWhenNeeded(params.branchId, { force: true }),
        ]).catch(() => undefined);
        if (orderId && detailTarget?.id === orderId) {
          const full = await getPurchaseOrderById(orderId);
          if (full) {
            setDetailTarget(full);
            await loadDetailExtras(orderId);
          } else {
            setDetailTarget(null);
          }
        } else {
          setDetailTarget(null);
        }
        showSuccessAfterModalClose('Mercadería recibida');
        await loadOrders();
        await loadSummary();
      } catch (err) {
        console.warn('[usePurchasesScreen] handleReceiveOrder', err);
        showErrorAfterModalClose('Error', err instanceof PurchaseDomainError ? err.message : 'No pude registrar la recepción.');
        reportUserVisibleFailure({ module: 'purchases', op: 'receive_order', error: err });
      } finally {
        setSubmitting(false);
      }
    },
    [detailTarget?.id, loadDetailExtras, loadOrders, loadSummary],
  );

  const handleOpenCompleteExpenseFiscal = useCallback((expense: ExpenseRow) => {
    setFiscalCompleteExpense(expense);
  }, []);

  const handleSaveCompleteExpenseFiscal = useCallback(
    async (fiscal: ExpenseFiscalInvoiceInput) => {
      if (!fiscalCompleteExpense) return;
      setSubmitting(true);
      try {
        await executeUpdateExpenseFiscalIntake({
          expenseId: fiscalCompleteExpense.id,
          branchId: fiscalCompleteExpense.branchId,
          fiscal,
        });
        setFiscalCompleteExpense(null);
        showSuccessAfterModalClose('Comprobante guardado');
        if (detailTarget) {
          const refreshed = await listExpensesByPurchaseOrderId(detailTarget.id);
          setLinkedExpenses(refreshed.filter((e) => e.status === 'active'));
          setLinkedExpense(refreshed.find((e) => e.status === 'active') ?? null);
        }
        await loadOrders();
      } catch (err) {
        console.warn('[usePurchasesScreen] handleSaveCompleteExpenseFiscal', err);
        showErrorAfterModalClose('Error', 'No pude guardar el comprobante.');
        reportUserVisibleFailure({ module: 'purchases', op: 'complete_expense_fiscal', error: err });
      } finally {
        setSubmitting(false);
      }
    },
    [detailTarget, fiscalCompleteExpense, loadOrders],
  );

  const handleSaveSupplierCreditNote = useCallback(
    async (fiscal: ExpenseFiscalInvoiceInput) => {
      if (!supplierCreditNoteModal) return;
      const organizationId = await getSessionOrganizationId();
      if (!organizationId) {
        showErrorAfterModalClose('Error', 'No encontré la organización.');
        return;
      }
      setSubmitting(true);
      try {
        await executeCreateExpenseIntake({
          organizationId,
          branchId: supplierCreditNoteModal.branchId,
          categorySlug: 'supplier_payment',
          amount: supplierCreditNoteModal.amount,
          expenseDate: todayIsoDate(),
          description: `NC proveedor · ${supplierCreditNoteModal.supplierName ?? 'Proveedor'}`,
          supplierId: supplierCreditNoteModal.supplierId,
          paymentStatus: 'unpaid',
          fiscal,
        });
        setSupplierCreditNoteModal(null);
        showSuccessAfterModalClose('NC del proveedor registrada');
        await pullExpensesWhenNeeded(supplierCreditNoteModal.branchId, { force: true }).catch(
          () => undefined,
        );
      } catch (err) {
        console.warn('[usePurchasesScreen] handleSaveSupplierCreditNote', err);
        showErrorAfterModalClose('Error', 'No pude registrar la NC.');
        reportUserVisibleFailure({ module: 'purchases', op: 'supplier_credit_note', error: err });
      } finally {
        setSubmitting(false);
      }
    },
    [supplierCreditNoteModal],
  );

  const openSupplierCreditNoteOffer = useCallback(
    async (params: {
      branchId: string;
      supplierId: string | null;
      supplierName: string | null;
      returnedAmount: number;
      orderId: string | null;
      receptionId: string | null;
    }) => {
      if (!fiscalCaptureEnabled || params.returnedAmount <= 0) return;
      const wantNc = await confirm({
        title: '¿Registrar NC del proveedor?',
        message:
          'La NC la emite tu proveedor. Acá registrás el comprobante para el Libro IVA cuando la tengas.',
        confirmLabel: 'Registrar NC',
        presentation: 'overlay',
      });
      if (!wantNc) return;
      let sourceInvoiceDocType: number | null = null;
      if (params.orderId) {
        const linked = await listExpensesByPurchaseOrderId(params.orderId);
        const source = linked.find(
          (e) =>
            e.status === 'active' &&
            (params.receptionId ? e.stockReceptionId === params.receptionId : true),
        );
        sourceInvoiceDocType = source?.invoiceDocType ?? null;
      }
      const ncDocType = creditNoteDocTypeFromPurchaseInvoice(sourceInvoiceDocType);
      const initialDraft: ExpenseFiscalDraft = {
        ...emptyExpenseFiscalDraft(),
        ...(ncDocType != null ? { docType: ncDocType } : {}),
      };
      setSupplierCreditNoteModal({
        branchId: params.branchId,
        supplierId: params.supplierId,
        supplierName: params.supplierName,
        amount: params.returnedAmount,
        initialDraft,
      });
    },
    [fiscalCaptureEnabled, confirm],
  );

  const handleRegisterSupplierCreditNoteFromReturn = useCallback(
    async (params: {
      returnId: string;
      receptionId: string | null;
      order: PurchaseOrderWithItems;
    }) => {
      const items = await listReturnItemsForReturn(params.returnId);
      const returnedAmount = items.reduce(
        (sum, item) => sum + item.quantity * (item.unitCost ?? 0),
        0,
      );
      await openSupplierCreditNoteOffer({
        branchId: params.order.branchId,
        supplierId: params.order.supplierId,
        supplierName: params.order.supplierName,
        returnedAmount,
        orderId: params.order.id,
        receptionId: params.receptionId,
      });
    },
    [openSupplierCreditNoteOffer],
  );

  // ─── Return order ────────────────────────────────────────────────────────
  // Una devolución por recepción (el modelo es por recepción, no por orden).
  const handleReturnOrder = useCallback(
    async (params: {
      branchId: string;
      supplierId: string | null;
      supplierName: string | null;
      reason: string;
      returnedBy?: string | null;
      groups: Array<{ receptionId: string; items: CreateReturnItemInput[] }>;
    }) => {
      setSubmitting(true);
      try {
        for (const group of params.groups) {
          await createPurchaseReturnIntake({
            returnId: crypto.randomUUID(),
            branchId: params.branchId,
            receptionId: group.receptionId,
            supplierId: params.supplierId,
            supplierName: params.supplierName,
            returnedBy: params.returnedBy,
            reason: params.reason,
            items: group.items,
          });
        }
        const orderIdForNc = returnTarget?.id ?? detailTarget?.id ?? null;
        setReturnTarget(null);
        setDetailTarget(null);
        showSuccessAfterModalClose(
          params.groups.length > 1 ? 'Devoluciones registradas' : 'Devolución registrada',
        );
        await loadOrders();
        await loadSummary();

        const returnedAmount = params.groups.reduce(
          (sum, group) =>
            sum +
            group.items.reduce(
              (itemSum, item) => itemSum + item.quantity * (item.unitCost ?? 0),
              0,
            ),
          0,
        );
        await openSupplierCreditNoteOffer({
          branchId: params.branchId,
          supplierId: params.supplierId,
          supplierName: params.supplierName,
          returnedAmount,
          orderId: orderIdForNc,
          receptionId: params.groups[0]?.receptionId ?? null,
        });
      } catch (err) {
        console.warn('[usePurchasesScreen] handleReturnOrder', err);
        showErrorAfterModalClose(
          'Error',
          'No pude registrar la devolución. Si había varias recepciones, puede que alguna haya quedado registrada.',
        );
        reportUserVisibleFailure({ module: 'purchases', op: 'return_order', error: err });
        await loadOrders();
        await loadSummary();
      } finally {
        setSubmitting(false);
      }
    },
    [loadOrders, loadSummary, openSupplierCreditNoteOffer, returnTarget, detailTarget],
  );

  const expectedCashInDrawer = useMemo(() => {
    // Server-authoritative (issue #750): usar expectedBalance del RPC cuando esté.
    // Fallback client-side para offline / ramOnly / migración no aplicada.
    if (cashSummary?.expectedBalance != null) return cashSummary.expectedBalance;
    if (!cashSummary?.activeSession) return null;
    return (
      (cashSummary.activeSession.openingAmount ?? 0) +
      (cashSummary.sessionCashTotal ?? 0) -
      (cashSummary.sessionOutflowsTotal ?? 0)
    );
  }, [
    cashSummary?.expectedBalance,
    cashSummary?.activeSession,
    cashSummary?.sessionCashTotal,
    cashSummary?.sessionOutflowsTotal,
  ]);

  const activeCashSessionInfo = useMemo(() => {
    if (!activeCashSession) return null;
    return { session: activeCashSession, operatorLabel: null };
  }, [activeCashSession]);

  const openPayForExpense = useCallback(
    async (expense: ExpenseRow) => {
      if (!canPay || !currentBranchId) {
        if (!canPay) showError('Sin permisos', 'No podés pagar gastos.');
        return;
      }
      try {
        const cash = await resolveCashSummary(currentBranchId);
        setActiveCashSession(cash.activeSession ?? null);
        setCashSummary(cash);
      } catch (err) {
        console.warn('[usePurchasesScreen] handleOpenPay cash', err);
        setActiveCashSession(null);
        setCashSummary(null);
        presentUnexpectedFailure({ module: 'purchases', op: 'resolve_cash_state', error: err, userMessage: 'No pude resolver el estado de la caja.' });
      }
      setLinkedExpense(expense);
      setSettleAmountText(String(expenseAmountResidual(expense.amount, expense.amountPaid)));
      setSettleOpen(true);
    },
    [canPay, currentBranchId],
  );

  const handleOpenPay = useCallback(
    async (expense?: ExpenseRow) => {
      const target = expense ?? linkedExpense;
      if (!target) return;
      await openPayForExpense(target);
    },
    [linkedExpense, openPayForExpense],
  );

  // Cuenta corriente: pagar el gasto pendiente más viejo del proveedor.
  const handlePaySupplierBalance = useCallback(
    async (supplierId: string) => {
      if (!currentBranchId) return;
      try {
        const expenses = await listExpensesForBranch({
          branchId: currentBranchId,
          supplierId,
        });
        const pending = expenses
          .filter(
            (e) =>
              e.status === 'active' &&
              (e.paymentStatus === 'unpaid' || e.paymentStatus === 'partial'),
          )
          .sort((a, b) => (a.dueDate ?? '9999-12-31').localeCompare(b.dueDate ?? '9999-12-31'));
        const target = pending[0];
        if (!target) {
          showError('Sin deuda', 'Este proveedor no tiene gastos pendientes de pago.');
          return;
        }
        await openPayForExpense(target);
      } catch (err) {
        presentUnexpectedFailure({ module: 'purchases', op: 'pay_supplier_balance', error: err, userMessage: 'No pude cargar los gastos del proveedor.' });
      }
    },
    [currentBranchId, openPayForExpense],
  );

  // Cartera de cheques para "pagar con cheque" (endoso Fase C).
  useEffect(() => {
    if (!settleOpen || !canPayWithCheck || !currentBranchId) {
      setPortfolioChecks([]);
      return;
    }
    let cancelled = false;
    setPortfolioChecksLoading(true);
    listChecksForBranch({ branchId: currentBranchId, statuses: ['in_portfolio'] })
      .then((rows) => {
        if (!cancelled) setPortfolioChecks(rows);
      })
      .catch((err) => {
        captureCatch('purchases', 'checks.list')(err);
        if (!cancelled) setPortfolioChecks([]);
      })
      .finally(() => {
        if (!cancelled) setPortfolioChecksLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [settleOpen, canPayWithCheck, currentBranchId]);

  const handleSettleExpense = useCallback(
    async (input: { method: ExpenseSettleMethod; note: string; paidDate: string; checkId?: string | null }) => {
      if (!canPay || !currentBranchId || !linkedExpense) {
        if (!canPay) showError('Sin permisos', 'No podés pagar gastos.');
        return;
      }
      // Endoso Fase C: el cheque en cartera paga el gasto (sin egreso de caja).
      if (input.method === 'check') {
        if (!input.checkId) {
          showError('Cheque', 'Elegí el cheque a endosar.');
          return;
        }
        setSubmitting(true);
        try {
          await applyCheckCommand({
            operation: 'endorse',
            checkId: input.checkId,
            settlementId: newClientUuid(),
            expenseId: linkedExpense.id,
            note: input.note.trim() || undefined,
            operationId: newClientUuid(),
          });
          setSettleOpen(false);
          showSuccessAfterModalClose('Gasto pagado con cheque');
          if (detailTarget) await loadDetailExtras(detailTarget.id);
          await loadOrders();
          await loadSummary();
        } catch (error: unknown) {
          const message = error instanceof Error ? error.message : 'No pude endosar el cheque.';
          showErrorAfterModalClose('Error', message);
          reportUserVisibleFailure({ module: 'purchases', op: 'endorse_check', error });
        } finally {
          setSubmitting(false);
        }
        return;
      }
      const amount = parseDecimalInput(settleAmountText, { min: 0.01 });
      if (amount == null) {
        showError('Monto inválido', 'Ingresá un monto mayor a cero.');
        return;
      }
      if (input.method === 'cash_drawer' && !activeCashSession?.id) {
        showError('Caja cerrada', 'Abrí un turno de caja para pagar en efectivo desde la caja.');
        return;
      }
      if (input.method === 'other' && !input.note.trim()) {
        showError('Nota requerida', 'Indicá cuándo y cómo se pagó.');
        return;
      }
      if (
        input.method === 'cash_drawer' &&
        expectedCashInDrawer != null &&
        amount > expectedCashInDrawer
      ) {
        const ok = await confirm({
          title: 'Sale más plata de la que hay en caja',
          message: `Querés sacar ${formatMoney(amount)} y en caja hay ${formatMoney(expectedCashInDrawer)}. ¿Lo registrás igual?`,
          confirmLabel: 'Registrar',
        });
        if (!ok) return;
      }
      const categorySlug = (linkedExpense.categorySlug ?? 'other') as ExpenseCategorySlug;
      setSubmitting(true);
      try {
        await executeSettleExpenseIntake({
          expenseId: linkedExpense.id,
          branchId: currentBranchId,
          method: input.method,
          amount,
          categorySlug,
          cashSessionId: input.method === 'cash_drawer' ? activeCashSession?.id ?? null : null,
          note: input.note.trim() || null,
          paidAt:
            input.method === 'cash_drawer'
              ? undefined
              : isoDateToPaidAt(input.paidDate || todayIsoDate()),
          createdBy: userId,
        });
        setSettleOpen(false);
        showSuccessAfterModalClose('Pago registrado');
        if (detailTarget) await loadDetailExtras(detailTarget.id);
        await loadOrders();
        await loadSummary();
      } catch (error: unknown) {
        showErrorAfterModalClose('Error', 'No pude registrar el pago.');
        reportUserVisibleFailure({ module: 'purchases', op: 'register_payment', error });
      } finally {
        setSubmitting(false);
      }
    },
    [
      canPay,
      currentBranchId,
      linkedExpense,
      activeCashSession?.id,
      expectedCashInDrawer,
      confirm,
      userId,
      loadDetailExtras,
      detailTarget,
      settleAmountText,
      loadOrders,
      loadSummary,
    ],
  );

  // BUG-45: Anular recepción desde el detail modal
  const handleVoidReception = useCallback(
    async (receptionId: string, voidReason: string) => {
      const linked = linkedExpenses.find(
        (e) => e.status === 'active' && e.stockReceptionId === receptionId,
      );
      let fiscalVoidAck = false;
      if (linked?.invoiceDocType != null) {
        const ok = await confirm({
          title: 'Anular recepción con factura fiscal',
          message:
            'Esta recepción tiene un comprobante en el Libro IVA compras. Si la anulás, el gasto queda anulado en Vuelto, pero la factura del proveedor sigue existiendo para AFIP hasta que registres la NC correspondiente.',
          confirmLabel: 'Anular igual',
          destructive: true,
        });
        if (!ok) return;
        fiscalVoidAck = true;
      }
      setSubmitting(true);
      try {
        await voidPurchaseReceptionIntake({
          receptionId,
          voidedBy: userId ?? null,
          voidReason,
          fiscalVoidAck,
        });
        showSuccess('Recepción anulada');
        if (detailTarget) await loadDetailExtras(detailTarget.id);
        await loadOrders();
        await loadSummary();
      } catch (err) {
        if (err instanceof PurchaseDomainError) {
          showError(err.message);
        } else {
          presentUnexpectedFailure({ module: 'purchases', op: 'void_reception', error: err, userMessage: 'No pude anular la recepción.' });
        }
      } finally {
        setSubmitting(false);
      }
    },
    [userId, detailTarget, loadDetailExtras, loadOrders, loadSummary, linkedExpenses, confirm],
  );

  // BUG-45: Anular devolución desde el detail modal
  const handleVoidReturn = useCallback(
    async (returnId: string, voidReason: string) => {
      setSubmitting(true);
      try {
        await voidPurchaseReturnIntake({
          returnId,
          voidedBy: userId ?? null,
          voidReason,
        });
        showSuccess('Devolución anulada');
        if (detailTarget) await loadDetailExtras(detailTarget.id);
        await loadOrders();
        await loadSummary();
      } catch (err) {
        if (err instanceof PurchaseDomainError) {
          showError(err.message);
        } else {
          presentUnexpectedFailure({ module: 'purchases', op: 'void_return', error: err, userMessage: 'No pude anular la devolución.' });
        }
      } finally {
        setSubmitting(false);
      }
    },
    [userId, detailTarget, loadDetailExtras, loadOrders, loadSummary],
  );

  // BUG-11: El modal ya pide motivo (two-click flow), no necesita confirm() adicional.
  const handleCloseLeftover = useCallback(
    async (orderId: string, closeReason: string) => {
      setSubmitting(true);
      try {
        await closePurchaseOrderIntake({
          purchaseOrderId: orderId,
          closedBy: userId,
          closeReason,
        });
        const full = await getPurchaseOrderById(orderId);
        setDetailTarget(full);
        showSuccess('Pedido cerrado');
        await loadOrders();
        await loadSummary();
      } catch (err) {
        if (err instanceof PurchaseDomainError) {
          showError(err.message);
        } else {
          presentUnexpectedFailure({ module: 'purchases', op: 'close_order', error: err, userMessage: 'No pude cerrar el pedido.' });
        }
      } finally {
        setSubmitting(false);
      }
    },
    [userId, loadOrders, loadSummary],
  );

  const handleCloseDetail = useCallback(() => {
    setDetailTarget(null);
    setLinkedExpense(null);
    setLinkedExpenses([]);
    setHasReturn(false);
    setSettleOpen(false);
  }, []);

  // ─── Duplicar OC anulada como borrador ───────────────────────────────────
  const handleDuplicateAsDraft = useCallback((order: PurchaseOrderWithItems) => {
    setRegisterMode('order');
    setRegisterPrefill({
      supplierId: order.supplierId,
      supplierName: order.supplierName,
      items: order.items.map((item, index) => ({
        id: `prefill-${index}-${item.id}`,
        productId: item.productId ?? '',
        productName: item.productName ?? 'Producto',
        productSku: item.productSku,
        quantity: String(item.quantity),
        unitCost: item.unitCost != null ? String(item.unitCost) : '',
        unitOfMeasure: item.unitOfMeasure,
        targetBranchId: item.targetBranchId ?? null,
        variantId: item.variantId ?? null,
        variantName: null,
      })),
    });
    setDetailTarget(null);
    setRegisterOpen(true);
  }, []);

  // ─── Limpiar búsqueda y filtros (estado "nada coincide") ────────────────
  const clearAllFilters = useCallback(() => {
    setSearch('');
    setStatusFilter('all');
    setQueueFilter('all');
    setOriginFilter('all');
    setSupplierFilter('all');
    setSortBy('date');
    setSortDir('desc');
  }, []);

  const totalPages = Math.max(1, Math.ceil(total / PURCHASES_LIST_PAGE_SIZE));

  return {
    canView,
    canEdit,
    canReceive,
    canReturn,
    canVoid,
    canPay,
    currentBranchId,
    userId,
    orders,
    total,
    loading,
    page,
    setPage,
    totalPages,
    search,
    setSearch,
    statusFilter,
    setStatusFilter: handleStatusFilterChange,
    queueFilter,
    setQueueFilter: handleQueueFilterChange,
    originFilter,
    setOriginFilter,
    supplierFilter,
    setSupplierFilter,
    // BUG-16: Sort state y handler
    sortBy,
    sortDir,
    setSortBy,
    setSortDir,
    clearAllFilters,
    summary,
    queueExpenses,
    loadFn,
    registerOpen,
    setRegisterOpen,
    registerMode,
    setRegisterMode,
    detailTarget,
    setDetailTarget,
    detailLoading,
    returnTarget,
    setReturnTarget,
    returnReceptions,
    setReturnReceptions,
    handleOpenReturn,
    transferOpen,
    setTransferOpen,
    handleStockTransfer,
    submitting,
    supplierAccountTarget,
    setSupplierAccountTarget,
    handleOpenDetail,
    handleOpenSupplierAccountPicker,
    handleCreateOrder,
    handleSendOrder,
    handleVoidOrder,
    handleReceiveOrder,
    handleReturnOrder,
    handleOpenPay,
    handlePaySupplierBalance,
    handleDuplicateAsDraft,
    registerPrefill,
    setRegisterPrefill,
    handleSettleExpense,
    handleCloseLeftover,
    handleCloseDetail,
    // BUG-45: Recepciones, devoluciones y handlers de anulación
    receptions: detailReceptions,
    returns: detailReturns,
    handleVoidReception,
    handleVoidReturn,
    linkedExpense,
    linkedExpenses,
    hasReturn,
    settleOpen,
    setSettleOpen,
    settleAmountText,
    setSettleAmountText,
    canPayWithCheck,
    portfolioChecks,
    portfolioChecksLoading,
    activeCashSessionInfo,
    expectedCashInDrawer,
    ConfirmModal,
    fiscalCaptureEnabled,
    fiscalCompleteExpense,
    setFiscalCompleteExpense,
    handleSaveCompleteExpenseFiscal,
    supplierCreditNoteModal,
    setSupplierCreditNoteModal,
    handleSaveSupplierCreditNote,
    handleRegisterSupplierCreditNoteFromReturn,
    handleOpenCompleteExpenseFiscal,
  };
}

export type PurchasesScreenVm = ReturnType<typeof usePurchasesScreen>;
